import React, { useState, useEffect } from 'react';
import { Form, Input, Select, InputNumber, Button, message, List, Popconfirm, Upload, Switch } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { getDomains, updateExploitation, uploadExploitationKmlFiles, calculateTotalSurface, checkKMLOverlap, getExploitationInfo, deleteExploitationFile } from '../../Utils/exploitations/exploitations';
import { getAllProducts } from '../../Utils/products/products';
import { getAllClientsReduced } from '../../Utils/client/client';
import moment from 'moment';
import CustomDatePicker from '../Shared/CustomDatePicker';
import { AlertCircle } from 'lucide-react';


const { Option } = Select;

const ExploitationDataForm = ({ clients, domains, products, loading, handleClientChange, verifySurface, isAutoCalculate, setIsAutoCalculate, isEmail }) => (
    <div className='grid grid-cols-2 gap-8'>
        {/* Form items for exploitation data */}
        <Form.Item name="exploitation_name" label="Nom d'exploitation" rules={[{ required: true, message: 'Le nom d\'exploitation est requis.' }]}>
            <Input />
        </Form.Item>

        <Form.Item name="manager_name" label="Gérant">
            <Input />
        </Form.Item>

        {/* disable edit if email exists and display a info msg*/}
        <div className="max-w-xl">
            <Form.Item
                name="email"
                label={
                    <span className="text-gray-700 font-medium">
                        Email
                    </span>
                }
                rules={[
                    { message: "L'email est requis." },
                    { type: 'email', message: 'Veuillez entrer un email valide.' }
                ]}
                className="mb-1"
            >
                <Input
                    disabled={isEmail}
                    className="rounded-md"
                    placeholder="Entrez votre email"
                />
            </Form.Item>

            {isEmail && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-md border border-amber-200">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-amber-700 text-sm">
                        L'email ne peut pas être modifié car un compte existe déjà pour cet email.
                        Pour modifier l'email, allez à l'option de change de mot de passe ou email.
                    </p>
                </div>
            )}
        </div>

        {/* if email doesnt exist we add a password field */}
        {!isEmail && (
            <>
                <Form.Item
                    name="password"
                    label="Mot de passe"
                    rules={[
                        { required: true, message: 'Le mot de passe est requis.' },
                        { min: 8, message: 'Le mot de passe doit contenir au moins 8 caractères.' }
                    ]}
                    hasFeedback
                >
                    <Input.Password autoComplete='new-password' />
                </Form.Item>

                <Form.Item
                    name="confirm_password"
                    label="Confirmer le mot de passe"
                    dependencies={['password']}
                    hasFeedback
                    rules={[
                        { required: true, message: 'Veuillez confirmer votre mot de passe.' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Les deux mots de passe ne correspondent pas.'));
                            },
                        }),
                    ]}
                >
                    <Input.Password autoComplete='new-password' />
                </Form.Item>
            </>
        )}

        <Form.Item name="user_id" label="Client" rules={[{ required: true, message: 'Le client est requis.' }]}>
            <Select loading={loading} showSearch placeholder="Sélectionnez un client" optionFilterProp="children" onChange={handleClientChange}
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                {clients.map(client => (
                    <Option key={client.user_id} value={client.user_id}>{client.company_name}</Option>
                ))}
            </Select>
        </Form.Item>

        <Form.Item name="domain_id" label="Domaine" rules={[{ required: true, message: 'Le domaine est requis.' }]}>
            <Select loading={loading} placeholder="Sélectionnez un domaine">
                {domains.map(domain => (
                    <Option key={domain.domain_id} value={domain.domain_id}>{domain.domain_name}</Option>
                ))}
            </Select>
        </Form.Item>

        <Form.Item name="region" label="Région">
            <Input />
        </Form.Item>

        <Form.Item name="city" label="Ville">
            <Input />
        </Form.Item>

        <Form.Item name="creation_date" label="Date de création" rules={[{ required: true, message: 'La date de création est requise.' }]}>
            <CustomDatePicker />
        </Form.Item>

        <Form.Item name="installation_date" label="Date d'installation">
            <CustomDatePicker />
        </Form.Item>


        <Form.Item
            label={
                <span>
                    Superficie brute (en ha)
                    <Switch
                        checkedChildren={<p>auto</p>}
                        unCheckedChildren={<p>manuelle</p>}
                        checked={isAutoCalculate}
                        onChange={(checked) => setIsAutoCalculate(checked)}
                        style={{ marginLeft: '10px' }}
                    />
                </span>
            }
        >

            <Form.Item name="exploitiation_superficie_brut" noStyle>
                <InputNumber className='w-full' disabled={isAutoCalculate} />
            </Form.Item>
        </Form.Item>

        <Form.Item name="exploitiation_superficie_net" label="Superficie nette (en ha)"
            rules={[
                { validator: verifySurface }
            ]}>
            <InputNumber className='w-full' />
        </Form.Item>

        <Form.Item name="product_id" label="Produit" rules={[{ required: true, message: 'Le produit est requis.' }]}>
            <Select loading={loading} showSearch placeholder="Sélectionnez un produit" optionFilterProp="children"
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                {products.map(product => (
                    <Option key={product.product_id} value={product.product_id}>{product.product_name}</Option>
                ))}
            </Select>
        </Form.Item>
    </div>
);

const FileManagementForm = ({ exploitationId, kmlFiles, setKmlFiles, setBrutSurface, setOverlapError, form, isAutoCalculate }) => {
    const [ignoreOverlap, setIgnoreOverlap] = useState(false);


    const fetchUpdatedFiles = async () => {
        try {
            const exploitationData = (await getExploitationInfo(exploitationId)).data;
            if (exploitationData.kml_files == '') {
                setKmlFiles([]);
                return;
            }
            const files = exploitationData.kml_files?.split(',');
            const updatedFiles = files.map((file, index) => ({
                uid: `-${index}`,
                name: file || `File ${index + 1}`,
                status: 'done',
                url: file,
            }));
            setKmlFiles(updatedFiles);
        } catch (error) {
            console.error('Error fetching updated files:', error);
            message.error('Erreur lors de la récupération des fichiers mis à jour');
        }
    };

    const handleFileUpload = async (file) => {
        try {
            const formData = new FormData();
            formData.append('files', file);

            await uploadExploitationKmlFiles(exploitationId, formData);

            message.success('Fichier KML ajouté avec succès');

            // Fetch updated file list
            await fetchUpdatedFiles();

            // Recalculate brut surface if auto-calculate is on
            if (isAutoCalculate) {
                calculateBrutSurface([...kmlFiles, file]);
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            message.error('Erreur lors de l\'ajout du fichier KML: ' + (error.message || 'Unknown error'));
        }
        return false; // Prevent default upload behavior
    };

    const handleFileDelete = async (fileName) => {
        try {
            await deleteExploitationFile(exploitationId, fileName);
            message.success('Fichier KML supprimé avec succès');

            // Fetch updated file list
            await fetchUpdatedFiles();

            // Recalculate brut surface if auto-calculate is on
            if (isAutoCalculate) {
                const updatedFiles = kmlFiles.filter(file => file.name !== fileName);
                calculateBrutSurface(updatedFiles);
            }
        } catch (error) {
            console.error('Error deleting file:', error);
            message.error('Erreur lors de la suppression du fichier KML: ' + (error.message || 'Unknown error'));
        }
    };

    const calculateBrutSurface = async (files) => {
        if (files?.length > 0) {
            try {
                const fileContents = await Promise.all(
                    files.map(async (file) => {
                        if (file.name) {
                            const response = await fetch(process.env.REACT_APP_KML_PATH + file.name);
                            if (!response.ok) {
                                throw new Error(`Failed to fetch ${file.name}: ${response.statusText}`);
                            }
                            const blob = await response.blob();

                            // Convert the Blob to a File
                            const fileObject = new File([blob], file.name, {
                                type: blob.type || '',
                                lastModified: Date.now()
                            });
                            return fileObject;
                        }

                        // If it's a local file object, return it as is
                        return file.originFileObj;
                    })
                );

                const surface = await calculateTotalSurface(fileContents);
                if (isAutoCalculate) {
                    setBrutSurface(surface);
                    form.setFieldsValue({ exploitiation_superficie_brut: surface });
                }


                // Only perform overlap check if not ignoring overlap
                if (!ignoreOverlap) {
                    const overlapResult = await checkKMLOverlap(fileContents);
                    if (overlapResult.hasOverlap) {
                        setOverlapError(`Chevauchement détecté dans les fichiers: <br> ${overlapResult.overlappingFiles.join('<br />')}`);
                    } else {
                        setOverlapError(null);
                    }
                } else {
                    setOverlapError(null);
                }
            } catch (error) {
                console.error('Error calculating surface:', error);
                message.error('Erreur lors du calcul de la surface brute');
                setBrutSurface(0);
                form.setFieldsValue({ exploitiation_superficie_brut: 0 });
                setOverlapError('Erreur lors du traitement des fichiers KML');
            }

        } else if (!isAutoCalculate) {
            // If auto-calculate is off, keep the current form value
            setBrutSurface(form.getFieldValue('exploitiation_superficie_brut') || 0);
        } else {
            setBrutSurface(0);
            form.setFieldsValue({ exploitiation_superficie_brut: 0 });
            setOverlapError(null);
        }
    };

    useEffect(() => {
        calculateBrutSurface(kmlFiles);
    }, [ignoreOverlap, isAutoCalculate, kmlFiles]);

    return (
        <>
            <div className='grid grid-cols-2 gap-4 p-4'>
                <List
                    dataSource={kmlFiles}
                    renderItem={(file) => (
                        <List.Item
                            actions={[
                                <Popconfirm
                                    title="Êtes-vous sûr de vouloir supprimer ce fichier ?"
                                    onConfirm={() => handleFileDelete(file.name)}
                                    okText="Oui"
                                    cancelText="Non"
                                >
                                    <DeleteOutlined />
                                </Popconfirm>
                            ]}
                        >
                            <List.Item.Meta
                                title={file.name}
                            />
                        </List.Item>
                    )}
                />

                <div className="border border-gray-300 h-fit rounded-md px-4 bg-gray-50">
                    <label className="inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={ignoreOverlap}
                            onChange={() => setIgnoreOverlap(!ignoreOverlap)}
                            className="form-checkbox h-5 w-5 text-blue-600 transition duration-150 ease-in-out"
                        />
                        <span className="p-2 text-lg font-medium text-gray-700">
                            Ignorer le chevauchement
                        </span>
                    </label>
                </div>
            </div>

            <Upload
                className='my-4'
                accept=".kml"
                multiple={false}
                fileList={[]}
                beforeUpload={handleFileUpload}
            >
                <Button icon={<UploadOutlined />}>Ajouter un fichier KML</Button>
            </Upload>
        </>
    );
};

const ExploitationEditPage = ({ exploitationId, onClose }) => {
    const [form] = Form.useForm();
    const [clients, setClients] = useState([]);
    const [domains, setDomains] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [brutSurface, setBrutSurface] = useState(0);
    const [kmlFiles, setKmlFiles] = useState([]);
    const [overlapError, setOverlapError] = useState(null);
    const [isAutoCalculate, setIsAutoCalculate] = useState(true);
    const [isEmail, setIsEmail] = useState(false);


    useEffect(() => {
        fetchClients();
        fetchProducts();
        fetchExploitationData();
    }, [exploitationId]);

    useEffect(() => {
        form.setFieldsValue({ valve_exploitiation_superficie_brut: brutSurface });
    }, [brutSurface, form]);

    const fetchExploitationData = async () => {
        try {
            const exploitationData = (await getExploitationInfo(exploitationId)).data;
            form.setFieldsValue({
                ...exploitationData,
                creation_date: moment(exploitationData.creation_date),
                installation_date: moment(exploitationData.installation_date),
                product_id: exploitationData.product ? exploitationData.product.product_id : undefined
            });
            if (exploitationData.manager_id) {
                setIsEmail(true);
            }

            if (exploitationData.kml_files == '') {
                setKmlFiles([]);
                return;
            }

            const files = exploitationData.kml_files?.split(',');

            const existingFileList = files?.map((file, index) => ({
                uid: `-${index}`,
                name: file || `File ${index + 1}`,
                status: 'done',
                url: file,
            }));
            setKmlFiles(existingFileList);

            fetchDomains(exploitationData.user_id);
        } catch (error) {
            console.error('Error fetching exploitation data:', error);
            message.error('Problème lors de la récupération des données d\'exploitation');
        }
    };

    const fetchClients = async () => {
        setLoading(true);
        try {
            const clientsData = (await getAllClientsReduced()).data;
            setClients(Array.isArray(clientsData) ? clientsData : []);
        } catch (error) {
            console.error('Error fetching clients:', error);
            message.error('Problème lors de la récupération des clients');
        } finally {
            setLoading(false);
        }
    };

    const fetchDomains = async (clientId) => {
        setLoading(true);
        try {
            const domainsData = await getDomains(clientId);
            setDomains(Array.isArray(domainsData) ? domainsData : []);
        } catch (error) {
            console.error('Error fetching domains:', error);
            message.error('Problème lors de la récupération des domaines');
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const productsData = await getAllProducts();
            setProducts(Array.isArray(productsData.data) ? productsData.data : []);
        } catch (error) {
            console.error('Error fetching products:', error);
            message.error('Problème lors de la récupération des produits');
        } finally {
            setLoading(false);
        }
    };

    const handleClientChange = (value) => {
        fetchDomains(value);
        form.setFieldsValue({ domain_id: undefined });
    };

    const verifySurface = (_, value) => {
        const netSurface = Number(value);
        if (isNaN(netSurface)) {
            return Promise.reject('La superficie nette doit être un nombre valide.');
        }
        if (netSurface > brutSurface) {
            return Promise.reject('La superficie nette ne peut pas être supérieure à la superficie brute.');
        }
        return Promise.resolve();
    };

    const onFinish = async (values) => {
        let data = {}
        Object.keys(values).forEach(key => {

            if (key === 'creation_date' || key === 'installation_date') {
                data[key] = values[key].toISOString();
            }
            else if (key !== 'files') {
                data[key] = values[key];
            }
        });

        try {
            await updateExploitation(exploitationId, data);
            message.success('Exploitation mise à jour avec succès');
        } catch (error) {
            console.error('Error updating exploitation:', error);
            message.error('La mise à jour de l\'exploitation a échoué');
        }
    };

    const handelvaluesChange = (changedValues, allValues) => {
        if (changedValues.exploitiation_superficie_brut && !isAutoCalculate) {
            setBrutSurface(changedValues.exploitiation_superficie_brut);
        }
    }

    return (
        <div className='flex flex-col justify-center p-8'>
            <h1 className='text-3xl pb-8 font-bold text-gray-800'>Modifier l'exploitation</h1>

            <Form form={form} onFinish={onFinish} layout="vertical" className='w-full' onValuesChange={handelvaluesChange}>
                <ExploitationDataForm
                    form={form}
                    clients={clients}
                    domains={domains}
                    products={products}
                    loading={loading}
                    handleClientChange={handleClientChange}
                    brutSurface={brutSurface}
                    verifySurface={verifySurface}
                    isAutoCalculate={isAutoCalculate}
                    setIsAutoCalculate={setIsAutoCalculate}
                    isEmail={isEmail}
                />
                <hr />

                <FileManagementForm
                    exploitationId={exploitationId}
                    kmlFiles={kmlFiles}
                    setKmlFiles={setKmlFiles}
                    setBrutSurface={setBrutSurface}
                    setOverlapError={setOverlapError}
                    form={form}
                    overlapError={overlapError}
                    isAutoCalculate={isAutoCalculate}
                />
                <hr className='mt-4' />
                {overlapError && <div className="text-red-500 mb-4" dangerouslySetInnerHTML={{ __html: overlapError }} />}


                <Form.Item className='py-6'>
                    <Button type="primary" htmlType="submit" disabled={!!overlapError}>Mettre à jour</Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default ExploitationEditPage;